import React, { useState, useMemo, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQueryGetMe } from "@/apis/users/getMe";
import { useQueryGetExamPaperReview } from "@/apis/exam_progress/getExamPaperReview";
import { useQueryGetHighlights } from "@/apis/questions";
import ModuleNavigationModal, {
  type ModuleInfo,
} from "@/components/exam/shared/ModuleNavigationModal";
import HighlightableContent from "@/pages/my_exams/components/HighlightableContent";
import ResultQuestionSelectorPopover from "./components/ResultQuestionSelectorPopover";
import ExamHeaderUI from "@/components/exam/ui/ExamHeaderUI";
import ExamFooterUI from "@/components/exam/ui/ExamFooterUI";
import ExamPassageUI from "@/components/exam/ui/ExamPassageUI";
import ExamQuestionPanelUI from "@/components/exam/ui/ExamQuestionPanelUI";
import {
  EXAM_PAPER_TYPE_READING_WRITING,
  EXAM_PAPER_TYPE_MATH,
} from "@/constants/exam";
import { getPaperTypeLabel } from "@/constants/exam";
import { getSectionLabel } from "@/utils/exam";

interface QuestionWithAnswer {
  id: string;
  passage?: string;
  question: string;
  choices: Array<{
    id: string;
    text: string;
  }>;
  answer_type: "multiple_choice" | "short_answer";
  correct_choice_id: string;
  correct_answer?: string; // For short answer questions
  category?: string;
  user_answer?: string;
  excluded_choices?: string[];
  marked_for_review?: boolean;
  is_correct?: boolean;
}

const ExamResultViewPage: React.FC = () => {
  const { id, paperId } = useParams<{ id: string; paperId: string }>();
  const { data: me, isLoading: isMeLoading } = useQueryGetMe();

  // Get exam paper review data
  const {
    data: examReview,
    isLoading: isReviewLoading,
    error: reviewError,
  } = useQueryGetExamPaperReview(id || "", paperId || "");

  // UI States
  const [moduleNavOpen, setModuleNavOpen] = useState(false);
  const [currentModuleId, setCurrentModuleId] = useState("1");
  const [currentQuestionIdx, setCurrentQuestionIdx] = useState(0);
  const [currentPaperId, setCurrentPaperId] = useState<string>("");
  const [questionSelectorOpen, setQuestionSelectorOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Get exam paper data for current module (temporary disabled)
  // TODO: Fix this when we have proper paper ID mapping
  const examPaper = null;
  const isPaperLoading = false;

  // Extract modules from exam review data
  const sectionLabel = getSectionLabel(examReview?.paper?.exam_type);
  const modules: ModuleInfo[] = useMemo(() => {
    if (!examReview?.sections) {
      return [
        { id: "1", title: `${sectionLabel} 1`, questionCount: 27 },
        { id: "2", title: `${sectionLabel} 2`, questionCount: 27 },
      ];
    }

    return examReview.sections.map((section) => ({
      id: section.section_no.toString(),
      title: `${sectionLabel} ${section.section_no}`,
      questionCount: section.question_count,
    }));
  }, [examReview, sectionLabel]);

  // Get questions with user answers from review data
  const questionsWithAnswers: QuestionWithAnswer[] = useMemo(() => {
    if (!examReview?.sections) {
      return [];
    }

    // Get current section based on currentModuleId
    const currentSection = examReview.sections.find(
      (section) => section.section_no.toString() === currentModuleId
    );

    if (!currentSection) {
      return [];
    }

    // Create a map of answers by question_id
    const answersMap = new Map();
    (currentSection.answers || []).forEach((answer) => {
      answersMap.set(answer.question_id, answer);
    });

    // Map questions with their corresponding answers
    return currentSection.questions.map((question) => {
      const answer = answersMap.get(question.id);
      return {
        id: question.id,
        passage: question.passage,
        question: question.question,
        choices: question.choices,
        answer_type: question.answer_type,
        correct_choice_id: question.correct_choice_id,
        correct_answer: answer?.correct_answer, // Add correct answer from API
        category: "Reading Comprehension", // TODO: Get category from category_id
        user_answer: answer?.selected_answer,
        excluded_choices: answer?.excluded_choices || [],
        marked_for_review: answer?.marked_for_review || false,
        is_correct: answer?.is_correct || false,
      };
    });
  }, [examReview, currentModuleId]);

  const currentQuestion = questionsWithAnswers[currentQuestionIdx];

  // Highlight data management
  const { data: highlightsData } = useQueryGetHighlights(
    id || "",
    currentQuestion?.id || ""
  );

  // Get highlighted content or fallback to original
  const highlightedPassage =
    highlightsData?.highlighted_passage || currentQuestion?.passage || "";
  const highlightedQuestion =
    highlightsData?.highlighted_question_text ||
    currentQuestion?.question ||
    "";

  // Set initial paper ID when modules are loaded
  React.useEffect(() => {
    if (modules.length > 0 && !currentPaperId) {
      // Use the paper ID from params
      setCurrentPaperId(paperId || "");
    }
  }, [modules, currentPaperId, paperId]);

  // Close popover on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        setQuestionSelectorOpen(false);
      }
    }
    if (questionSelectorOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [questionSelectorOpen]);

  const handleModuleSelect = (moduleId: string) => {
    setCurrentModuleId(moduleId);
    setCurrentQuestionIdx(0);
    // TODO: Update currentPaperId based on selected module
  };

  const handleQuestionSelect = (idx: number) => {
    setCurrentQuestionIdx(idx);
  };

  // Calculate question states for popover
  const answeredQuestions = useMemo(() => {
    return questionsWithAnswers
      .map((q, idx) => (q.user_answer ? idx : -1))
      .filter((idx) => idx !== -1);
  }, [questionsWithAnswers]);

  const forReviewQuestions = useMemo(() => {
    return questionsWithAnswers
      .map((q, idx) => (q.marked_for_review ? idx : -1))
      .filter((idx) => idx !== -1);
  }, [questionsWithAnswers]);

  // Get choice text by choice ID
  const getChoiceText = (choiceId: string) => {
    if (!currentQuestion?.choices) return choiceId;
    const choice = currentQuestion.choices.find((c) => c.id === choiceId);
    return choice ? `${choiceId}. ${choice.text}` : choiceId;
  };

  // Prepare directions content
  const directionsContent = useMemo(() => {
    if (!examReview) return null;

    // Get subject from exam review data
    const subject =
      examReview.paper?.subject || EXAM_PAPER_TYPE_READING_WRITING;

    if (subject === EXAM_PAPER_TYPE_READING_WRITING) {
      return (
        <p className="text-lg">
          The questions in this section address a number of important reading
          and writing skills. Each question includes one or more passages, which
          may include a table or graph. Read each passage and question
          carefully, and then choose the best answer to the question based on
          the passage(s).
          <br />
          <br />
          All questions in this section are multiple-choice with four answer
          choices. Each question has a single best answer.
        </p>
      );
    }

    if (subject === EXAM_PAPER_TYPE_MATH) {
      return (
        <p className="text-lg">
          The questions in this section address a number of important math
          skills.
          <br />
          Use of a calculator is permitted for all questions. A reference sheet,
          calculator, and these directions can be accessed throughout the test.
          <br />
          Unless otherwise indicated:
          <br />
          • All variables and expressions represent real numbers.
          <br />
          • Figures provided are drawn to scale.
          <br />
          • All figures lie in a plane.
          <br />• The domain of a given function f is the set of all real
          numbers x for which f(x) is a real number.
        </p>
      );
    }

    return null;
  }, [examReview]);

  // Loading states
  if (isMeLoading || isReviewLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#f7f8fa]">
        <div className="text-lg">Loading exam result...</div>
      </div>
    );
  }

  // Error states
  if (reviewError) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#f7f8fa]">
        <div className="text-center">
          <div className="text-red-600 mb-4">Error loading exam result</div>
          <button
            onClick={() => window.history.back()}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Check if paper is completed
  if (examReview?.paper_status?.status !== "completed") {
    return (
      <div className="h-screen flex items-center justify-center bg-[#f7f8fa]">
        <div className="text-center">
          <div className="text-yellow-600 mb-4">
            This paper is not completed yet
          </div>
          <button
            onClick={() => window.history.back()}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Get current subject for header
  const currentSubject = examReview?.paper?.exam_type || "SAT";
  const currentSubjectName =
    examReview?.paper?.subject || EXAM_PAPER_TYPE_READING_WRITING;
  const sectionTitle = `${getPaperTypeLabel(
    currentSubjectName
  )}, ${getSectionLabel(currentSubject)} ${currentModuleId}`;

  return (
    <div className="flex flex-col h-screen bg-[#f7f8fa]">
      {/* Header */}
      <ExamHeaderUI
        sectionTitle={sectionTitle}
        directionsContent={directionsContent}
        mode="result"
        showControls={false}
        onSwitchModule={() => setModuleNavOpen(true)}
      />

      {/* Main Content */}
      <main className="flex-1 flex h-0 min-h-0 overflow-hidden bg-white justify-center">
        <ExamPassageUI
          passage={highlightedPassage}
          subject={currentSubjectName}
          examType={currentSubject}
          answerType={currentQuestion?.answer_type}
          mode="result"
        />
        <ExamQuestionPanelUI
          questionNumber={currentQuestionIdx + 1}
          questionText={currentQuestion?.question || ""}
          answerType={
            (currentQuestion?.answer_type as
              | "multiple_choice"
              | "short_answer") || "multiple_choice"
          }
          choices={currentQuestion?.choices}
          selectedChoice={currentQuestion?.user_answer}
          excludedChoices={currentQuestion?.excluded_choices}
          markForReview={currentQuestion?.marked_for_review}
          correctChoiceId={
            currentQuestion?.answer_type === "multiple_choice"
              ? currentQuestion?.correct_choice_id
              : currentQuestion?.correct_answer
          }
          userAnswer={currentQuestion?.user_answer}
          isCorrect={currentQuestion?.is_correct}
          category={currentQuestion?.category}
          mode="result"
          highlightableContent={
            <div className="prose prose-lg leading-relaxed">
              <div
                className="quill-content whitespace-pre-wrap text-[18px] text-gray-900 leading-relaxed font-serif font-times [&_img]:block [&_img]:mx-auto"
                style={{ fontFamily: '"Times New Roman", Times, serif' }}
                dangerouslySetInnerHTML={{ __html: highlightedQuestion }}
              />
            </div>
          }
        />
      </main>

      {/* Footer */}
      <ExamFooterUI
        studentName={me?.name || "Student"}
        currentQuestionIdx={currentQuestionIdx}
        totalQuestions={questionsWithAnswers.length}
        mode="result"
        showQuestionSelector={true}
        answeredQuestions={answeredQuestions}
        forReviewQuestions={forReviewQuestions}
        questionSelectorOpen={questionSelectorOpen}
        onToggleQuestionSelector={() =>
          setQuestionSelectorOpen(!questionSelectorOpen)
        }
        onQuestionSelect={handleQuestionSelect}
        onPrev={() => handleQuestionSelect(Math.max(0, currentQuestionIdx - 1))}
        onNext={() =>
          handleQuestionSelect(
            Math.min(questionsWithAnswers.length - 1, currentQuestionIdx + 1)
          )
        }
        disablePrev={currentQuestionIdx === 0}
        disableNext={currentQuestionIdx === questionsWithAnswers.length - 1}
        nextButtonText="Next"
        questionSelectorContent={
          <ResultQuestionSelectorPopover
            isOpen={questionSelectorOpen}
            onClose={() => setQuestionSelectorOpen(false)}
            sectionTitle={sectionTitle}
            totalQuestions={questionsWithAnswers.length}
            answeredQuestions={answeredQuestions}
            forReviewQuestions={forReviewQuestions}
            onQuestionSelect={handleQuestionSelect}
            currentQuestionIdx={currentQuestionIdx}
          />
        }
      />

      {/* Module Navigation Modal */}
      <ModuleNavigationModal
        isOpen={moduleNavOpen}
        onClose={() => setModuleNavOpen(false)}
        modules={modules}
        currentModuleId={currentModuleId}
        onModuleSelect={handleModuleSelect}
      />
    </div>
  );
};

export default ExamResultViewPage;
